/**
 * ReviewSuggestionsCard — Suggestions d'avis outillées (a3)
 *
 * Placée en tête de /reseau/avis-vendeurs :
 * - Liste les vendeurs de mandats clos ≥ 3 j sans invitation active
 * - Un-clic pour envoyer l'invitation (appel /seller-reviews/invitations)
 * - Affiche le taux de réponse global sur 180 j
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Send, Loader2, Sparkles, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function ReviewSuggestionsCard({ onSent }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sendingId, setSendingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/reseau/reviews/suggestions?limit=8');
            setData(r.data);
        } catch (e) {
            // fail silencieux
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const send = async (item) => {
        const key = `${item.mandate_id}:${item.seller_email}`;
        setSendingId(key);
        try {
            const r = await api.post('/seller-reviews/invitations', {
                mandate_id: item.mandate_id,
                seller_email: item.seller_email,
                eligible_event: item.eligible_event || 'sale_closed',
            });
            if (r.data?.already_sent) toast.info('Une invitation est déjà en cours pour ce vendeur.');
            else if (r.data?.sent) toast.success('Invitation envoyée');
            else toast.warning('Invitation créée — vérifiez l\'envoi e-mail');
            await load();
            if (onSent) onSent();
        } catch (e) {
            toast.error("Impossible d'envoyer l'invitation");
        } finally {
            setSendingId(null);
        }
    };

    if (loading) {
        return (
            <div className="reseau-card p-4 mb-4 animate-pulse"
                 data-testid="review-suggestions-loading">
                <div className="h-4 w-40 bg-black/10 rounded mb-3" />
                <div className="h-14 w-full bg-black/5 rounded" />
            </div>
        );
    }

    const items = data?.items || [];
    const metrics = data?.metrics || {};
    if (!items.length && !metrics.invitations_sent) return null;

    return (
        <section className="reseau-card p-5 mb-4"
                 data-testid="review-suggestions-card">
            <header className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                         style={{ background: 'rgba(104,23,147,0.1)', color: 'var(--reseau-violet)' }}>
                        <Sparkles className="w-4 h-4" />
                    </div>
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider"
                           style={{ color: 'var(--reseau-violet)' }}>
                            Suggestions d'invitations d'avis
                        </p>
                        <h3 className="text-base font-bold">
                            {items.length > 0
                                ? `${items.length} vendeur${items.length > 1 ? 's' : ''} à solliciter`
                                : 'Aucune suggestion en attente'}
                        </h3>
                        <p className="text-xs mt-0.5"
                           style={{ color: 'var(--reseau-ink-muted)' }}>
                            Dossiers clos depuis {data?.delay_days} jours ou plus, sans invitation active.
                        </p>
                    </div>
                </div>
                {metrics.invitations_sent > 0 && (
                    <div className="text-right"
                         data-testid="review-suggestions-metrics">
                        <div className="inline-flex items-center gap-1 text-xs font-semibold"
                             style={{ color: 'var(--reseau-ink-muted)' }}>
                            <TrendingUp className="w-3.5 h-3.5" />
                            Taux de réponse
                        </div>
                        <p className="text-2xl font-extrabold tabular-nums leading-tight mt-0.5"
                           style={{ color: 'var(--reseau-ink)' }}>
                            {metrics.response_rate_percent == null
                                ? '—'
                                : `${metrics.response_rate_percent}%`}
                        </p>
                        <p className="text-[10px]"
                           style={{ color: 'var(--reseau-ink-muted)' }}>
                            {metrics.reviews_submitted}/{metrics.invitations_sent} · 180 j
                        </p>
                    </div>
                )}
            </header>

            {items.length > 0 && (
                <ul className="space-y-2" data-testid="review-suggestions-list">
                    {items.map((it) => {
                        const key = `${it.mandate_id}:${it.seller_email}`;
                        const sending = sendingId === key;
                        return (
                            <li key={key}
                                data-testid={`review-suggestion-${it.mandate_id}`}
                                className="flex items-center justify-between gap-3 p-3 rounded-lg border"
                                style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium truncate"
                                       style={{ color: 'var(--reseau-ink)' }}>
                                        {it.seller_first_name || 'Vendeur'}
                                        {it.seller_last_name_initial && ` ${it.seller_last_name_initial}.`}
                                        {it.city && (
                                            <span className="ml-2 text-xs font-normal"
                                                  style={{ color: 'var(--reseau-ink-muted)' }}>
                                                · {it.city}
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-[11px]"
                                       style={{ color: 'var(--reseau-ink-muted)' }}>
                                        Dossier clos il y a {it.days_since_closed} j · statut {it.mandate_status}
                                    </p>
                                </div>
                                <button type="button"
                                        onClick={() => send(it)}
                                        disabled={sending}
                                        data-testid={`review-suggestion-invite-${it.mandate_id}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                                        style={{ background: 'var(--reseau-violet)', color: '#fff',
                                                 opacity: sending ? 0.7 : 1 }}>
                                    {sending
                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        : <Send className="w-3.5 h-3.5" />}
                                    Inviter en un clic
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
